/**
 * Owns whichever record is on the platter, and the choreography that replaces
 * it: lift the arm, slide the old record away, drop the new one in, let it
 * settle. A swap requested while one is running cancels the first.
 */

import type * as THREE from 'three';
import { DIM } from '../geometry/dims.ts';
import { YAW_REST } from '../geometry/tonearm.ts';
import type { Palette } from '../lib/palette.ts';
import { Token } from '../lib/tween.ts';
import type { ArmControl } from './arm-control.ts';
import type { DeckState } from './deck-state.ts';
import { createRecord, type RecordMesh } from './record.ts';
import { flyIn, flyOut, isCancelled, type SwapArt, seatRecord, settle } from './record-flight.ts';

export interface SwapOptions {
  cinematic?: boolean;
  pictureDisc?: boolean;
}

export interface Swapper {
  /** The record currently on the platter. */
  readonly disc: RecordMesh;
  /** The record sliding away, while one is in flight. */
  readonly outgoing: RecordMesh | null;
  swap(image: CanvasImageSource | null, palette: Palette | null, o?: SwapOptions): Promise<void>;
}

/**
 * Owns whichever record is on the platter, and the choreography that replaces
 * it. A swap requested while one is running cancels the first.
 */
export function createSwapper(
  renderer: THREE.WebGLRenderer,
  root: THREE.Group,
  state: DeckState,
  arm: ArmControl,
): Swapper {
  let disc = createRecord(renderer);
  disc.carrier.position.set(DIM.platter.x, DIM.recordY, DIM.platter.z);
  root.add(disc.carrier);

  let outgoing: RecordMesh | null = null;
  let token: Token | null = null;

  const dropOutgoing = (): void => {
    if (!outgoing) return;
    root.remove(outgoing.carrier);
    outgoing.dispose();
    outgoing = null;
  };

  return {
    get disc() {
      return disc;
    },
    get outgoing() {
      return outgoing;
    },

    async swap(image, palette, { cinematic = true, pictureDisc = false } = {}) {
      token?.cancel();
      const mine = new Token();
      token = mine;
      const art: SwapArt = { image, palette, pictureDisc };
      dropOutgoing();

      if (!cinematic) {
        seatRecord(disc, art);
        state.seated = true;
        return;
      }

      try {
        state.seated = false;
        state.armFollow = false;
        mine.track(arm.setArmDown(false, 260));
        state.armTargetYaw = YAW_REST;

        outgoing = disc;
        await flyOut(outgoing, mine);
        dropOutgoing();

        disc = createRecord(renderer);
        disc.setArtwork(art.image, art.palette, art.pictureDisc);
        root.add(disc.carrier);
        await flyIn(disc, mine);
        await settle(disc, mine);

        seatRecord(disc, art);
        state.seated = true;
      } catch (e) {
        if (!isCancelled(e)) throw e;
      } finally {
        if (token === mine) token = null;
      }
    },
  };
}
